import React, { useEffect, useRef, useState } from 'react';

import { Avatar, Button, Link, Modal, ModalBody, ModalFooter, ModalHeader, Text } from '@change/ds-core-ui';
import { QuotationIcon } from '@change/ds-icons';

import { Translate } from '@change-corgi/core/react/i18n';

import type { TopicPageSupporterVoicesTextComment } from 'src/app/pages/topic/pageContext';

import { useSupporterVoices } from '../../hooks/useSupporterVoices';
import { MessageLabel } from '../MessageLabel';

type TextMessageProp = {
	supporterVoice: TopicPageSupporterVoicesTextComment;
};

export const TextMessage: React.FC<TextMessageProp> = ({
	supporterVoice: { text: message, firstName: user, petitionSlug, petitionTitle, id: commentId },
}) => {
	const textRef = useRef<HTMLDivElement>(null);
	const [textClamped, setTextClamped] = useState<boolean>(false);

	const {
		data: { topicName },
	} = useSupporterVoices();

	useEffect(() => {
		const textEl = textRef.current;

		if (textEl) {
			const clamped = textEl.scrollHeight > textEl.clientHeight;
			setTextClamped(clamped);
		}
	}, [message]);

	return (
		<div data-testid="supporter-voices-text" data-qa="supporter-voices-text-message">
			<div className="flex h-[326px] w-[75vw] max-w-[336px] flex-col gap-4 rounded-xl bg-background-raised p-6 text-typography-strong">
				<QuotationIcon size={40} className="text-icon-strong" />
				<Text ref={textRef} className="line-clamp-[7] italic" expressive>
					{message}
				</Text>

				{textClamped && (
					<Modal
						trigger={
							<Link role="button" as="button" data-qa="text-message-show-more-link">
								<Translate value="corgi.topic_page.supporter_voices.show_text" />
							</Link>
						}
					>
						<ModalHeader leadingSlot={<Avatar size="large" src="" alt={user} />} title={user} />
						<ModalBody className="p-6">{message}</ModalBody>
						<ModalFooter>
							<Button data-qa="modal-close-button" variant="secondary">
								<Translate value="corgi.topic_page.supporter_voices.close_modal" />
							</Button>
						</ModalFooter>
					</Modal>
				)}
			</div>
			<MessageLabel
				name={user}
				petition={{
					href: `/p/${petitionSlug}?utm_source=topic_${topicName}_supporter_voices_text_comment_${commentId}`,
					title: petitionTitle,
				}}
			/>
		</div>
	);
};
